import React, { useState, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Button, message, Table, DatePicker, Select, Space, Tag } from 'antd';
import { FileExcelOutlined, ClearOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as XLSX from 'xlsx';
import dayjs, { Dayjs } from 'dayjs';

// One admin change to a unit budget - from the Unit Budget (Admin) panel or one of the two
// Unit Manager Budget uploads - with the values before and after.
interface UnitBudgetHistoryRecord {
  id: string;
  changedAt: string;
  action: 'Created' | 'Updated' | 'Deleted';
  source: string;
  unitHeadName: string;
  unitHeadEmail: string | null;
  country: string | null;
  oldAwardsCount: number | null;
  oldAwardsUsed: number | null;
  oldSpan: number | null;
  newAwardsCount: number | null;
  newAwardsUsed: number | null;
  newSpan: number | null;
  comments: string | null;
  changedByName: string | null;
  changedByEmail: string | null;
}

const fmt = (value: number | null) => (value === null || value === undefined ? '' : String(Math.round(value * 100) / 100));

// "1.5 → 2", or just the value when it did not change.
const change = (oldValue: number | null, newValue: number | null) => {
  if (oldValue === null || oldValue === undefined) return fmt(newValue);
  if (newValue === null || newValue === undefined) return fmt(oldValue);
  return oldValue === newValue ? fmt(newValue) : `${fmt(oldValue)} → ${fmt(newValue)}`;
};

const balanceAfter = (r: UnitBudgetHistoryRecord) =>
  r.newAwardsCount === null ? null : Math.round(((r.newAwardsCount || 0) - (r.newAwardsUsed || 0)) * 100) / 100;

const actionColor: Record<string, string> = { Created: 'green', Updated: 'blue', Deleted: 'red' };

const UnitBudgetHistory: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [reportData, setReportData] = useState<UnitBudgetHistoryRecord[]>([]);
  const [showReport, setShowReport] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedUnitHead, setSelectedUnitHead] = useState<string | null>(null);
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [selectedChangedBy, setSelectedChangedBy] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  const isValidDateRange = () => !!startDate && !!endDate && !endDate.isBefore(startDate, 'day');

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      message.error('Please select both start and end dates');
      return;
    }
    if (endDate.isBefore(startDate, 'day')) {
      message.error('End date must be on or after start date');
      return;
    }

    setLoading(true);
    try {
      const token = await fetchToken();
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/budget/unit/history`, {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD')
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setReportData(response.data);
      setShowReport(true);
      setCurrentPage(1);
      setSelectedUnitHead(null);
      setSelectedSource(null);
      setSelectedChangedBy(null);
      setSelectedCountry(null);
    } catch (error) {
      message.error(`Failed to get unit budget history: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setReportData([]);
    setShowReport(false);
    setSelectedUnitHead(null);
    setSelectedSource(null);
    setSelectedChangedBy(null);
    setSelectedCountry(null);
    setCurrentPage(1);
  };

  const optionsOf = (values: (string | null)[]) =>
    Array.from(new Set(values.filter((v): v is string => !!v))).sort().map(v => ({ label: v, value: v }));

  const unitHeadFilters = useMemo(() => optionsOf(reportData.map(r => r.unitHeadName)), [reportData]);
  const sourceFilters = useMemo(() => optionsOf(reportData.map(r => r.source)), [reportData]);
  const changedByFilters = useMemo(() => optionsOf(reportData.map(r => r.changedByName || r.changedByEmail)), [reportData]);
  const countryFilters = useMemo(() => optionsOf(reportData.map(r => r.country)), [reportData]);

  const filteredData = useMemo(() => reportData.filter(r =>
    (!selectedUnitHead || r.unitHeadName === selectedUnitHead) &&
    (!selectedSource || r.source === selectedSource) &&
    (!selectedChangedBy || (r.changedByName || r.changedByEmail) === selectedChangedBy) &&
    (!selectedCountry || r.country === selectedCountry)
  ), [reportData, selectedUnitHead, selectedSource, selectedChangedBy, selectedCountry]);

  const handleDownloadExcel = () => {
    if (filteredData.length === 0) {
      message.error('No data available to download');
      return;
    }

    const formattedData = filteredData.map(r => ({
      'Changed At': dayjs(r.changedAt).format('YYYY-MM-DD HH:mm'),
      'Unit Head': r.unitHeadName,
      'Country': r.country || '',
      'Action': r.action,
      'Source': r.source,
      'Old Total GEM Awards': r.oldAwardsCount ?? '',
      'New Total GEM Awards': r.newAwardsCount ?? '',
      'Old Awards Used': r.oldAwardsUsed ?? '',
      'New Awards Used': r.newAwardsUsed ?? '',
      'Old Span': r.oldSpan ?? '',
      'New Span': r.newSpan ?? '',
      'Balance After': balanceAfter(r) ?? '',
      'Changed By': r.changedByName || '',
      'Changed By Email': r.changedByEmail || '',
      'Comments': r.comments || '',
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'UnitBudgetHistory');
    XLSX.writeFile(workbook, `UnitBudgetHistory_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  const columns = [
    {
      title: 'Changed At',
      dataIndex: 'changedAt',
      key: 'changedAt',
      width: 150,
      defaultSortOrder: 'descend' as const,
      sorter: (a: UnitBudgetHistoryRecord, b: UnitBudgetHistoryRecord) => dayjs(a.changedAt).unix() - dayjs(b.changedAt).unix(),
      render: (value: string) => dayjs(value).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: 'Unit Head',
      dataIndex: 'unitHeadName',
      key: 'unitHeadName',
      sorter: (a: UnitBudgetHistoryRecord, b: UnitBudgetHistoryRecord) => (a.unitHeadName || '').localeCompare(b.unitHeadName || ''),
    },
    { title: 'Country', dataIndex: 'country', key: 'country', width: 120 },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 100,
      render: (value: string) => <Tag color={actionColor[value]}>{value}</Tag>,
    },
    { title: 'Source', dataIndex: 'source', key: 'source', width: 230 },
    {
      title: 'Total GEM Awards',
      key: 'awardsCount',
      width: 140,
      render: (_: any, r: UnitBudgetHistoryRecord) => change(r.oldAwardsCount, r.newAwardsCount),
    },
    {
      title: 'Awards Used',
      key: 'awardsUsed',
      width: 120,
      render: (_: any, r: UnitBudgetHistoryRecord) => change(r.oldAwardsUsed, r.newAwardsUsed),
    },
    {
      title: 'Span',
      key: 'span',
      width: 100,
      render: (_: any, r: UnitBudgetHistoryRecord) => change(r.oldSpan, r.newSpan),
    },
    {
      title: 'Balance After',
      key: 'balance',
      width: 110,
      render: (_: any, r: UnitBudgetHistoryRecord) => {
        const balance = balanceAfter(r);
        return balance === null ? '' : <span style={{ color: balance < 0 ? '#d03b3b' : undefined, fontWeight: 600 }}>{balance}</span>;
      },
    },
    {
      title: 'Changed By',
      key: 'changedBy',
      render: (_: any, r: UnitBudgetHistoryRecord) => r.changedByName || r.changedByEmail || '-',
    },
    { title: 'Comments', dataIndex: 'comments', key: 'comments', ellipsis: true },
  ];

  const card = { backgroundColor: 'white', padding: '16px', borderRadius: '8px', marginBottom: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
  const label = { marginBottom: '6px', fontWeight: 500, color: '#595959', fontSize: '13px' };

  return (
    <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <div style={card}>
        <Space size="middle" wrap>
          <div>
            <div style={label}>Start Date</div>
            <DatePicker value={startDate} onChange={setStartDate} format="YYYY-MM-DD" style={{ width: '180px' }} />
          </div>
          <div>
            <div style={label}>End Date</div>
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              format="YYYY-MM-DD"
              style={{ width: '180px' }}
              disabledDate={(current) => !!startDate && current && current.isBefore(startDate, 'day')}
            />
          </div>
          <div style={{ paddingTop: '22px' }}>
            <Space size="small">
              <Button type="primary" onClick={handleSubmit} disabled={!isValidDateRange()} loading={loading}>
                Get Unit Budget History
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleDownloadExcel}
                style={filteredData.length === 0 ? {} : { backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }}
                disabled={filteredData.length === 0}
              >
                Download Excel
              </Button>
              <Button icon={<ClearOutlined />} onClick={handleClear} disabled={reportData.length === 0} danger>
                Clear
              </Button>
            </Space>
          </div>
        </Space>
      </div>
      {showReport && (
        <div>
          <div style={card}>
            <Space size="middle" wrap>
              <div>
                <div style={label}>Unit Head</div>
                <Select placeholder="All unit heads" style={{ width: 220 }} allowClear showSearch
                  options={unitHeadFilters} value={selectedUnitHead} onChange={setSelectedUnitHead} />
              </div>
              <div>
                <div style={label}>Source</div>
                <Select placeholder="All sources" style={{ width: 300 }} allowClear
                  options={sourceFilters} value={selectedSource} onChange={setSelectedSource} />
              </div>
              <div>
                <div style={label}>Changed By</div>
                <Select placeholder="All admins" style={{ width: 220 }} allowClear showSearch
                  options={changedByFilters} value={selectedChangedBy} onChange={setSelectedChangedBy} />
              </div>
              <div>
                <div style={label}>Country</div>
                <Select placeholder="All countries" style={{ width: 180 }} allowClear showSearch
                  options={countryFilters} value={selectedCountry} onChange={setSelectedCountry} />
              </div>
            </Space>
          </div>
          <div style={{ ...card, marginBottom: 0 }}>
            <Table
              dataSource={filteredData}
              columns={columns}
              rowKey="id"
              size="middle"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} changes`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
              }}
              scroll={{ x: 1400 }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitBudgetHistory;
